import { useTranslation } from 'react-i18next'
import { CheckCircle2, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { LevelProgress, LevelProgressRequirement } from '@/lib/sdk-official/resources/gamification'
import { actionLabelKey } from '@/components/features/members/gamificationActions'
import { readableCode } from '@/components/features/workflows/triggerBlocks'

/**
 * F-152 criterion 22: the week of a person for their level ("Para continuar em
 * Kernel nesta semana": each requirement as "Criar publicação: 2 de 3") and for
 * the next one ("Para subir para Root": the points missing and its requirements
 * this week). The API sends `levelProgress` only to the person and the team,
 * and only with the gamification on; a ladder without requirements on either
 * level has nothing about the week, so nothing shows.
 * A requirement in a space the viewer cannot open comes with `spaceHidden`
 * (SEC-309) and reads as "in a space you cannot access".
 */
export const LevelProgressCard = ({ progress, own, className }: { progress?: LevelProgress | null; own: boolean; className?: string }) => {
  const { t, i18n } = useTranslation(['ranking', 'badges'])
  if (!progress?.level) return null
  const keep = progress.level.requirements
  const next = progress.next
  if (!keep.length && !next?.requirements.length) return null
  const num = (n: number) => new Intl.NumberFormat(i18n.language).format(n)
  const missing = next ? Math.max(0, next.minPoints - progress.points) : 0

  /* Both blocks count this week: it decides the level on Monday (keeping) and right away (moving up). */
  const row = (r: LevelProgressRequirement, done: number, i: number) => {
    const key = actionLabelKey(r.actionType)
    const action = key ? t(key) : readableCode(r.actionType)
    const ok = done >= r.minCount
    return (
      <li key={`${r.actionType}-${r.spaceId ?? ''}-${i}`} className="flex items-start gap-2 text-sm" data-progress-requirement data-done={ok}>
        {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" aria-hidden /> : <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-300" aria-hidden />}
        <span className={cn('min-w-0 flex-1', ok ? 'text-gray-500' : 'text-gray-800')}>
          {r.spaceHidden
            ? t('progress.inHiddenSpace', { action })
            : r.spaceName
              ? t('progress.inSpace', { action, space: r.spaceName })
              : action}
        </span>
        <span className={cn('whitespace-nowrap tabular-nums', ok ? 'text-emerald-700' : 'font-medium text-gray-900')}>
          {t('progress.count', { done: num(Math.min(done, r.minCount)), goal: num(r.minCount) })}
        </span>
        {ok && <span className="sr-only">{t('progress.done')}</span>}
      </li>
    )
  }

  return (
    <section className={cn('space-y-4', className)} data-testid="level-progress">
      {keep.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-900">{t(own ? 'progress.keepOwn' : 'progress.keep', { name: progress.level.name })}</p>
          <p className="mb-2 text-xs text-gray-500">{t('progress.keepHint')}</p>
          <ul className="space-y-1.5">{keep.map((r, i) => row(r, r.thisWeek, i))}</ul>
        </div>
      )}
      {next && next.requirements.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-900">{t('progress.up', { name: next.name })}</p>
          <p className="mb-2 text-xs text-gray-500">
            {missing > 0 ? t('progress.pointsMissing', { points: t('badges:ranking.points', { count: missing, formatted: num(missing) }) }) : t('progress.pointsReached')}
          </p>
          <ul className="space-y-1.5">{next.requirements.map((r, i) => row(r, r.thisWeek, i))}</ul>
        </div>
      )}
      {next && !next.requirements.length && (
        <p className="text-xs text-gray-500">
          {missing > 0 ? t('progress.upPointsOnly', { name: next.name, points: t('badges:ranking.points', { count: missing, formatted: num(missing) }) }) : null}
        </p>
      )}
    </section>
  )
}
